import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import CoopNetLogo from '../components/CoopNetLogo';
import TurnstileWidget from '../components/TurnstileWidget';

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!turnstileToken) {
      setError('Espera a que se complete la verificación de seguridad.');
      return;
    }

    setLoading(true);
    const result = await login(email, password, turnstileToken);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
      // Un error de Turnstile invalida el token ya usado — hay que
      // esperar a que el widget emita uno nuevo antes de reintentar.
      if (result.turnstileError) setTurnstileToken('');
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: alpha(theme.palette.grey[100], 0.5),
      }}
    >
      {/* Left Side - Branding */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          width: '45%',
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.secondary.dark} 100%)`,
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          p: 6,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3,
          },
        }}
      >
        {/* Floating circles */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '10%',
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: alpha('#fff', 0.1),
            animation: 'float 6s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(-20px)' },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            right: '15%',
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: alpha('#fff', 0.08),
            animation: 'float 8s ease-in-out infinite',
            animationDelay: '1s',
          }}
        />

        <Box sx={{ position: 'relative', textAlign: 'center', color: 'white', maxWidth: 450 }}>
          {/* Logo */}
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: 4,
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 4,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
          >
            <CoopNetLogo size={76} />
          </Box>

          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            CoopNet
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 300, mb: 4, opacity: 0.9 }}>
            Red de Cooperativas para Economías Colaborativas
          </Typography>

          {/* Estructura jerárquica visual */}
          <Box sx={{ mt: 6 }}>
            <Typography variant="overline" sx={{ opacity: 0.7, letterSpacing: 2 }}>
              Estructura de 4 Niveles
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
              {[
                { label: 'Confederación Internacional', icon: '🌍', count: '' },
                { label: 'Federaciones Nacionales', icon: '🏛️', count: '' },
                { label: 'Cooperativas', icon: '🤝', count: '' },
                { label: 'Afiliados', icon: '👥', count: '' },
              ].map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 2,
                    background: alpha('#fff', 0.1),
                    backdropFilter: 'blur(5px)',
                    ml: index * 2,
                  }}
                >
                  <Typography variant="h6">{item.icon}</Typography>
                  <Box sx={{ flex: 1, textAlign: 'left' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.label}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Right Side - Login Form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: { xs: 3, sm: 4 },
          overflow: 'auto',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 440 }}>
          {/* Mobile Logo */}
          <Box
            sx={{
              display: { xs: 'flex', lg: 'none' },
              flexDirection: 'column',
              alignItems: 'center',
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <CoopNetLogo size={52} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              CoopNet
            </Typography>
          </Box>

          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, textAlign: { xs: 'center', md: 'left' } }}>
            Acceso al Sistema
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, textAlign: { xs: 'center', md: 'left' } }}>
            Inicia sesión con tu cuenta del ecosistema
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Card sx={{ p: 3 }}>
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Correo electrónico"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Widget Cloudflare visible (~300x65px) — modo Managed, ver TurnstileWidget.jsx */}
              <TurnstileWidget onToken={setTurnstileToken} />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !turnstileToken}
                endIcon={!loading && <ArrowIcon />}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Iniciar Sesión'}
              </Button>
            </form>
          </Card>

          <Typography variant="body2" sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
            Sistema Multi-Tenant para Redes Cooperativas
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
